"use client";

import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { formatDuration } from "./format";

/**
 * Pick a `[start, end]` sub-range of a known duration. Two sliders give fine
 * control and (when `onSeek` is wired) scrub the preview as you drag; the
 * "set to playhead" buttons capture the current preview position as the in/out
 * point — the intuitive way to clip while watching. Keeps `start < end`.
 *
 * The `<audio>`/`<video>` preview itself is rendered by the caller; this only
 * reads/drives its playhead through the callbacks.
 */
export function TimeRangeSelector({
  duration,
  start,
  end,
  current,
  onChange,
  onSeek,
  onUsePlayheadStart,
  onUsePlayheadEnd,
  disabled = false,
}: {
  duration: number;
  start: number;
  end: number;
  /** Current preview playhead, in seconds (for the readout). */
  current?: number;
  onChange: (start: number, end: number) => void;
  /** Seek the preview to a time (called while dragging a handle). */
  onSeek?: (time: number) => void;
  /** Capture the playhead as the start / end point. */
  onUsePlayheadStart?: () => void;
  onUsePlayheadEnd?: () => void;
  disabled?: boolean;
}) {
  const t = useTranslations("mediaTools.timeRange");
  const step = 0.1;
  const minGap = step;

  const setStart = (value: number) => {
    const next = Math.max(0, Math.min(value, end - minGap));
    onChange(next, end);
    onSeek?.(next);
  };
  const setEnd = (value: number) => {
    const next = Math.min(duration, Math.max(value, start + minGap));
    onChange(start, next);
    onSeek?.(next);
  };

  const showPlayheadControls = onUsePlayheadStart && onUsePlayheadEnd;

  return (
    <div className="space-y-4">
      {showPlayheadControls && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={onUsePlayheadStart}
          >
            {t("setStartToPlayhead")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={onUsePlayheadEnd}
          >
            {t("setEndToPlayhead")}
          </Button>
          {current !== undefined && (
            <span className="text-sm text-foreground/60">
              {t("playhead")} <span className="font-mono">{formatDuration(current)}</span>
            </span>
          )}
        </div>
      )}

      <div className="space-y-1">
        <Label htmlFor="trim-start" className="flex justify-between">
          <span>{t("start")}</span>
          <span className="font-mono text-foreground/70">{formatDuration(start)}</span>
        </Label>
        <input
          id="trim-start"
          type="range"
          className="w-full accent-foreground"
          min={0}
          max={duration}
          step={step}
          value={start}
          disabled={disabled}
          onChange={(event) => setStart(Number(event.target.value))}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="trim-end" className="flex justify-between">
          <span>{t("end")}</span>
          <span className="font-mono text-foreground/70">{formatDuration(end)}</span>
        </Label>
        <input
          id="trim-end"
          type="range"
          className="w-full accent-foreground"
          min={0}
          max={duration}
          step={step}
          value={end}
          disabled={disabled}
          onChange={(event) => setEnd(Number(event.target.value))}
        />
      </div>
      <p className="text-sm text-foreground/70">
        {t("keeping", {
          start: formatDuration(start),
          end: formatDuration(end),
          duration: formatDuration(Math.max(0, end - start)),
        })}
      </p>
    </div>
  );
}
